import React from 'react';
import {DEFAULT_PAGE_SIZE, DEFAULT_SORT_COLUMN, PAGE, REFERRAL_STATUS} from 'placement/const';
import {Pagination, Referral, SortBy, SortOrder} from 'placement/domains';
import {NavigationFilterValue} from '../domains/NavigationFilterService';
import {subMonths} from "date-fns";

export type ReferralPageAction =
    | { type: 'SET_PAGE'; page: PAGE }
    | { type: 'SET_PAGE_WITH_SORT_BY'; page: PAGE, sortBy: SortBy }
    | { type: 'SET_SORT_BY'; sortBy: SortBy }
    | { type: 'SET_FETCH_REFERRAL_COMPLETE'; pagination: Pagination, referrals: Array<Referral> }
    | { type: 'SET_CURRENT_CURSOR'; currentCursor: string | null }
    | { type: 'SET_FILTER'; filter: NavigationFilterValue }
    | { type: 'SET_SHOW_MODAL'; selectedReferral: Referral }
    | { type: 'SET_SHOW_MATCH_MODAL' }
    | { type: 'SET_HIDE_MATCH_MODAL' }
    | { type: 'SET_HIDE_MODAL' }
    | { type: 'SET_FETCH_SELECTED_OFFER_DONE'; selectedOfferGuids: Array<string> }
    | { type: 'SET_MATCH_OFFER_DONE'; }
    | { type: 'TOGGLE_OFFER_CONVERSATION_ONLY' }

export interface ReferralPageActionState {
    page: PAGE;
    sortBy: SortBy;
    pagination: Pagination;
    currentCursor: string | null;
    filter: NavigationFilterValue;
    showDetailModal: boolean;
    showMatchModal: boolean;
    selectedReferral: Referral | null;
    referrals: Array<Referral>;
    selectedOfferGuids: Array<string>;
    offerConversationOnly: boolean;
}

export function initialState(): ReferralPageActionState {
    return {
        page: PAGE.OPEN_REFERRALS,
        sortBy: {
            column: DEFAULT_SORT_COLUMN as keyof Referral,
            order: SortOrder.DESC,
        },
        pagination: {
            pageSize: DEFAULT_PAGE_SIZE,
            startCursor: '0',
            endCursor: '0',
            hasNextPage: false,
            hasPreviousPage: false,
            total: 0
        },
        filter: {
            scopeOfService: '',
            preferredCity: '',
            escalationLevels: '',
            referralConsiderations: '',
            requiredLanguage: '',
            serviceType: '',
            provider: '',
            serviceLocationTypes: '',
            counties: '',
            referralSubmittedFrom: null,
            referralSubmittedTo: null,
        },
        currentCursor: null,
        showDetailModal: false,
        showMatchModal: false,
        selectedReferral: null,
        referrals: [],
        selectedOfferGuids: [],
        offerConversationOnly: false,
    };
}

export function setPage(dispatch: React.Dispatch<ReferralPageAction>) {
    return function (page: string) {
        if ((page as PAGE) === PAGE.MATCHED_REFERRALS) {
            dispatch({
                type: 'SET_PAGE_WITH_SORT_BY',
                page: page as PAGE,
                sortBy: {column: "matchedDate", order: SortOrder.ASC}
            });
        } else {
            dispatch({type: 'SET_PAGE', page: page as PAGE});
        }
    };
}

export function setFilter(dispatch: React.Dispatch<ReferralPageAction>) {
    return function (filter: NavigationFilterValue) {
        dispatch({type: 'SET_FILTER', filter: filter});
    };
}

export function referralPageReducer(
    state: ReferralPageActionState,
    action: ReferralPageAction,
): ReferralPageActionState {
    switch (action.type) {
        case 'SET_PAGE':
            if (action.page === PAGE.OPEN_REFERRALS) {
                return {
                    ...state,
                    showDetailModal: false,
                    currentCursor: null,
                    page: action.page,
                    filter: {...state.filter, provider: '', referralSubmittedFrom: null, referralSubmittedTo: null},
                    offerConversationOnly: false
                };
            }

            return {
                ...state,
                filter: {
                    ...state.filter,
                    referralSubmittedFrom: action.page === PAGE.PROVIDER_REQUEST ? null : subMonths(new Date(), 1),
                    referralSubmittedTo: null
                },
                showDetailModal: false,
                currentCursor: null,
                page: action.page,
                offerConversationOnly: false
            };
        case 'SET_PAGE_WITH_SORT_BY':
            return {
                ...state,
                filter: {...state.filter, referralSubmittedFrom: subMonths(new Date(), 1), referralSubmittedTo: null},
                showDetailModal: false,
                currentCursor: null,
                page: action.page,
                sortBy: action.sortBy,
                offerConversationOnly: false
            }
        case 'SET_SORT_BY':
            return {
                ...state,
                showDetailModal: false,
                sortBy: action.sortBy
            };
        case 'SET_FETCH_REFERRAL_COMPLETE':
            return {
                ...state,
                referrals: action.referrals,
                pagination: action.pagination,
            };
        case 'SET_FILTER':
            return {
                ...state,
                filter: action.filter,
                showDetailModal: false,
                currentCursor: '0' // reset pagination on apply filter
            }
        case 'SET_CURRENT_CURSOR':
            return {
                ...state,
                currentCursor: action.currentCursor,
                showDetailModal: false
            }
        case 'SET_SHOW_MODAL':
            return {
                ...state,
                showDetailModal: true,
                selectedReferral: action.selectedReferral,
            }
        case 'SET_HIDE_MODAL':
            return {
                ...state,
                showDetailModal: false,
                selectedReferral: null
            }
        case 'SET_SHOW_MATCH_MODAL':
            return {
                ...state,
                showMatchModal: true,
            }
        case 'SET_HIDE_MATCH_MODAL':
            return {
                ...state,
                showMatchModal: false
            }
        case 'SET_FETCH_SELECTED_OFFER_DONE':
            return {
                ...state,
                selectedOfferGuids: action.selectedOfferGuids,
            }
        case 'SET_MATCH_OFFER_DONE':
            return {
                ...state,
                showMatchModal: false,
                referrals: state.referrals.map((referral) => {
                    return {
                        ...referral,
                        status: state.selectedOfferGuids.filter(
                            (offerGuid) => offerGuid === referral?.matchedOffer?.guid).length > 0 ?
                            REFERRAL_STATUS.MATCHED : referral.status,
                    }
                }),
                selectedOfferGuids: [],
            }
        case 'TOGGLE_OFFER_CONVERSATION_ONLY':
            return {
                ...state,
                offerConversationOnly: !state.offerConversationOnly,
                showDetailModal: false,
                currentCursor: '0'
            }
        default:
            return state;
    }
}
